import classNames from 'classnames';
import { useCombobox } from 'downshift';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'wouter';

import CloseSvg from '@/components/common/CloseSvg';

interface BrandItem {
    DimensionId: string;
    Name: string;
    UrlSlug: string;
}

interface BrandsSearchProps {
    brandsData: BrandItem[];
    onSearchChange: (query: string, finalizeSearch: boolean) => void;
    searchInputRef: React.RefObject<HTMLInputElement>;
}

const BrandsSearch: React.FC<BrandsSearchProps> = ({ brandsData, onSearchChange, searchInputRef }) => {
    const [inputValue, setInputValue] = useState('');
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [location] = useLocation();
    const prevDimension = useRef<string | null>(null);

    const updateQuery = useCallback(
        (query: string, shouldOpenDropdown = true, finalizeSearch = false) => {
            setInputValue(query);
            setDropdownOpen(shouldOpenDropdown && query.length > 0);
            onSearchChange(query, finalizeSearch);
        },
        [onSearchChange],
    );

    useEffect(() => {
        let category = window.location.pathname.substring(window.location.pathname.lastIndexOf('/') + 1);
        if (category === 'brand') {
            category = '';
        }
        if (prevDimension.current !== category) {
            prevDimension.current = category;
            updateQuery('', false);
        }
    }, [location, updateQuery]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newInputValue = event.target.value;
        setInputValue(newInputValue);
        onSearchChange(newInputValue, false);
        setDropdownOpen(newInputValue.length > 0);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            updateQuery(inputValue, false, true);
        }
    };

    const handleBlur = () => {
        setDropdownOpen(false);
    };

    const highlightMatch = (itemName: string, query: string) => {
        if (!query) return [itemName];
        const normalizedItemName = itemName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const normalizedQuery = query.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const regex = new RegExp(`(${normalizedQuery})`, 'gi');
        const parts = normalizedItemName.split(regex);
        return parts;
    };

    const { isOpen, getMenuProps, getInputProps, getItemProps, highlightedIndex, selectedItem } =
        useCombobox<BrandItem>({
            items: brandsData,
            inputValue,
            onInputValueChange: ({ inputValue }) => {
                setInputValue(inputValue || '');
                setDropdownOpen((inputValue || '').length > 0);
            },
            onSelectedItemChange: ({ selectedItem }) => {
                updateQuery(selectedItem ? selectedItem.Name : '', false, true);
                setTimeout(() => {
                    setDropdownOpen(false);
                });
            },
            itemToString: (item) => (item ? item.Name : ''),
        });

    return (
        <div className="relative inline-block min-w-[30%] max-md:min-w-full">
            <div className="relative border-x-0 border-b border-t-0 border-solid border-black">
                <input
                    {...getInputProps({
                        placeholder: 'Search all brands',
                        onChange: handleInputChange,
                        onKeyDown: handleKeyDown,
                        onBlur: handleBlur,
                        value: inputValue,
                        ref: searchInputRef,
                        className: 'w-full max-w-[80%] pb-1 text-base border-none focus:outline-none',
                    })}
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                    {inputValue && (
                        <>
                            <button
                                onClick={() => {
                                    updateQuery('', false);
                                }}
                                className="mb-0.5 focus:outline-none"
                                aria-label="Clear search"
                            >
                                <CloseSvg />
                            </button>

                            <div className="mb-1.5 ml-2 mr-1 h-4 w-px bg-black"></div>
                        </>
                    )}
                    <button
                        onClick={() => updateQuery(inputValue, false, true)}
                        className="focus:outline-none"
                        aria-label="Search"
                    >
                        <svg
                            viewBox="0 0 41 41"
                            width="28"
                            height="28"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                        >
                            <path d="M37.39 32.08L24.71 25.2a11.54 11.54 0 10-.9 1.16l12.84 7a.73.73 0 00.7-1.28zm-22.11-3.3a9.89 9.89 0 119.89-9.89 9.9 9.9 0 01-9.89 9.89z"></path>
                        </svg>
                    </button>
                </div>
            </div>
            <ul
                {...getMenuProps()}
                className={`absolute z-50 mt-1 w-full overflow-hidden ${
                    isDropdownOpen ? 'z-50 border border-solid border-black bg-white p-1' : 'hidden'
                }`}
            >
                {isOpen && brandsData.length > 0
                    ? brandsData.map((item, index) => (
                          // eslint-disable-next-line react/jsx-key
                          <li
                              {...getItemProps({
                                  key: item.UrlSlug,
                                  index,
                                  item,
                                  className: classNames('z-10 px-3 py-2 pb-2', {
                                      'bg-gray-200': highlightedIndex === index,
                                      'font-bold': selectedItem === item,
                                  }),
                              })}
                          >
                              {highlightMatch(item.Name, inputValue).map((part, index) =>
                                  part
                                      .toLowerCase()
                                      .normalize('NFD')
                                      .replace(/[\u0300-\u036f]/g, '') ===
                                  inputValue
                                      .toLowerCase()
                                      .normalize('NFD')
                                      .replace(/[\u0300-\u036f]/g, '') ? (
                                      <strong key={index}>{part}</strong>
                                  ) : (
                                      part
                                  ),
                              )}
                          </li>
                      ))
                    : inputValue && (
                          <li className="p-1 text-xs">No brand results found for &quot;{inputValue}&quot;</li>
                      )}
            </ul>
        </div>
    );
};

export default BrandsSearch;
